import { fetchMe, openSignIn } from "@bn-games/auth-client";
import { isDiscordActivity } from "@bn-games/discord-activity";
import { listFriends, type Friend, type OnlineGame } from "@bn-games/social-client";
import { t } from "@bn-games/i18n-core";
import { clear, el } from "./dom";
import { createCrestButton } from "./CrestButton";
import { createFriendPickerRow } from "./FriendPickerRow";
import { openModal } from "./Modal";
import { getLandingUrl } from "./landingUrl";

// The Capacitor global is only injected by a native iOS/Android shell — same
// zero-dependency proxy for "embedded shell" as MenuCorners.ts / InviteBanner.ts,
// and duplicated from them for the same reason: five lines with no dependency
// worth wiring a module around.
function isCapacitorNative(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.())
  );
}

export interface FriendPickerOptions {
  readonly game: OnlineGame;
  readonly roomCode: string;
}

/**
 * "Invite friend" for the room-waiting screen. `null` inside a Discord
 * Activity or a Capacitor native shell — same guard as `mountMenuCorners` /
 * `mountInviteBanner`: a room code invite is a web-sharing concept that makes
 * no sense once the platform already puts everyone in the same room (Discord)
 * or is a packaged app rather than a page with a URL (Capacitor).
 */
export function createInviteFriendButton(opts: FriendPickerOptions): HTMLElement | null {
  if (isDiscordActivity() || isCapacitorNative()) {
    return null;
  }
  return createCrestButton({
    label: t("social.friendPicker.button"),
    small: true,
    onClick: () => openFriendPicker(opts),
  });
}

function renderStatus(container: HTMLElement, text: string): void {
  clear(container);
  container.appendChild(el("p", { class: "friend-picker__status", attrs: { role: "status" }, text }));
}

/**
 * Two provider buttons via `openSignIn`, not a `<a href="/signin">` link —
 * there is no `/signin` route to navigate to; sign-in is a popup (or an iOS
 * PWA redirect) handled entirely by `@bn-games/auth-client`, the same as
 * `AuthWidget.ts`'s equivalent buttons.
 */
function renderSignedOut(container: HTMLElement, opts: FriendPickerOptions): void {
  clear(container);
  container.appendChild(
    el("p", { class: "friend-picker__status", text: t("social.friendPicker.signInPrompt") }),
  );
  container.appendChild(
    el("button", {
      class: "friend-picker__provider-btn friend-picker__provider-btn--google",
      text: t("auth.google"),
      on: {
        click: () => {
          void openSignIn("google").finally(() => { void load(container, opts); });
        },
      },
    }),
  );
  container.appendChild(
    el("button", {
      class: "friend-picker__provider-btn friend-picker__provider-btn--github",
      text: t("auth.github"),
      on: {
        click: () => {
          void openSignIn("github").finally(() => { void load(container, opts); });
        },
      },
    }),
  );
}

function renderError(container: HTMLElement, message: string): void {
  clear(container);
  container.appendChild(
    el("p", { class: "friend-picker__status friend-picker__status--error", attrs: { role: "alert" }, text: message }),
  );
}

function renderEmpty(container: HTMLElement): void {
  clear(container);
  container.appendChild(
    el("p", { class: "friend-picker__status", text: t("social.friendPicker.empty") }),
  );
  container.appendChild(
    el("a", {
      class: "friend-picker__link",
      text: t("social.friendPicker.findFriends"),
      attrs: { href: `${getLandingUrl()}/friends` },
    }),
  );
}

function renderFriends(container: HTMLElement, friends: readonly Friend[], opts: FriendPickerOptions): void {
  clear(container);
  for (const friend of friends) {
    container.appendChild(createFriendPickerRow({ friend, game: opts.game, roomCode: opts.roomCode }));
  }
}

/** Runs the fetchMe → listFriends sequence and renders the outcome into `container`. */
async function load(container: HTMLElement, opts: FriendPickerOptions): Promise<void> {
  renderStatus(container, t("social.friendPicker.loading"));

  const profile = await fetchMe();
  if (profile === null) {
    renderSignedOut(container, opts);
    return;
  }

  const result = await listFriends();
  if (!result.ok) {
    renderError(container, result.error.message);
    return;
  }

  if (result.value.length === 0) {
    renderEmpty(container);
    return;
  }

  renderFriends(container, result.value, opts);
}

/**
 * Opens the invite modal and does every async step *inside* it, rather than
 * gating the button itself on a `fetchMe()` call before opening — that would
 * need its own mount/re-render machinery the way `InviteBanner.ts` has. The
 * button always renders outside Discord/Capacitor; this modal is what
 * discovers a signed-out session.
 */
export function openFriendPicker(opts: FriendPickerOptions): void {
  const list = el("div", { class: "friend-picker__list", attrs: { "aria-live": "polite" } });

  const modal = openModal({
    title: t("social.friendPicker.modalTitle"),
    body: list,
    actions: [{ label: t("social.close"), onClick: () => modal.close() }],
  });

  void load(list, opts);
}
